import React from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  Code,
  Eye,
  Image,
  Indent,
  Italic,
  Link,
  List,
  ListOrdered,
  Minus,
  Outdent,
  Redo,
  Strikethrough,
  Table,
  Underline,
  Undo,
  Video,
} from "lucide-react";
import { ColorPicker } from "./ColorPicker";
import { EditorMode, ToolbarConfig, ToolbarButtonType } from "./types";

interface ToolbarProps {
  onCommand: (command: string, value?: string) => void;
  onModeToggle: () => void;
  onFullscreenToggle: () => void;
  mode: EditorMode;
  isFullscreen: boolean;
  disableImages?: boolean;
  disableVideos?: boolean;
  disableTables?: boolean;
  activeFormats?: Set<string>;
  currentFontName?: string;
  currentFontSize?: string;
  currentFormatBlock?: string;
  currentForeColor?: string;
  currentBackColor?: string;
  toolbarConfig?: ToolbarConfig;
}

export const Toolbar = ({
  onCommand,
  onModeToggle,
  onFullscreenToggle,
  mode,
  isFullscreen,
  disableImages = false,
  disableVideos = false,
  disableTables = false,
  activeFormats = new Set(),
  currentFontName = "",
  currentFontSize = "",
  currentFormatBlock = "p",
  currentForeColor = "#000000",
  currentBackColor = "#ffffff",
  toolbarConfig,
}: ToolbarProps) => {
  // Default toolbar configuration
  const defaultConfig: ToolbarConfig = {
    sections: [
      {
        name: "history",
        buttons: ["undo", "redo"],
      },
      {
        name: "formatting",
        buttons: ["formatBlock", "fontName", "fontSize"],
      },
      {
        name: "text-style",
        buttons: [
          "bold",
          "italic",
          "underline",
          "strikethrough",
          "foreColor",
          "backColor",
        ],
      },
      {
        name: "alignment",
        buttons: [
          "justifyLeft",
          "justifyCenter",
          "justifyRight",
          "justifyFull",
        ],
      },
      {
        name: "lists",
        buttons: [
          "insertUnorderedList",
          "insertOrderedList",
          "indent",
          "outdent",
        ],
      },
      {
        name: "insert",
        buttons: [
          "createLink",
          "insertImage",
          "insertVideo",
          "insertTable",
          "insertHorizontalRule",
        ],
      },
      {
        name: "view",
        buttons: ["modeToggle"],
      },
    ],
    showSeparators: true,
    hiddenButtons: [],
  };

  const config = toolbarConfig || defaultConfig;
  const hiddenButtons = new Set(config.hiddenButtons || []);

  const toolbarButton = (
    Icon: React.ComponentType<{ className?: string }>,
    tooltip: string,
    command: string,
    value?: string
  ) => {
    const isActive = activeFormats.has(command);
    return (
      <Button
        type="button"
        variant={isActive ? "secondary" : "ghost"}
        size="sm"
        onClick={() => onCommand(command, value)}
        title={tooltip}
        className={cn(
          "h-8 w-8 p-0 transition-smooth",
          isActive
            ? "bg-primary/10 text-primary hover:bg-primary/20"
            : "hover:bg-toolbar-hover hover:text-primary"
        )}
      >
        <Icon className="h-4 w-4" />
      </Button>
    );
  };

  const renderButton = (buttonType: ToolbarButtonType) => {
    if (hiddenButtons.has(buttonType)) return null;

    switch (buttonType) {
      case "undo":
        return toolbarButton(Undo, "Undo", "undo");
      case "redo":
        return toolbarButton(Redo, "Redo", "redo");
      case "bold":
        return toolbarButton(Bold, "Bold", "bold");
      case "italic":
        return toolbarButton(Italic, "Italic", "italic");
      case "underline":
        return toolbarButton(Underline, "Underline", "underline");
      case "strikethrough":
        return toolbarButton(Strikethrough, "Strikethrough", "strikethrough");
      case "justifyLeft":
        return toolbarButton(AlignLeft, "Align Left", "justifyLeft");
      case "justifyCenter":
        return toolbarButton(AlignCenter, "Align Center", "justifyCenter");
      case "justifyRight":
        return toolbarButton(AlignRight, "Align Right", "justifyRight");
      case "justifyFull":
        return toolbarButton(AlignJustify, "Justify", "justifyFull");
      // case "insertUnorderedList":
      //   return toolbarButton(List, "Bullet List", "insertUnorderedList");
      // case "insertOrderedList":
      //   return toolbarButton(ListOrdered, "Numbered List", "insertOrderedList");
      case "indent":
        return toolbarButton(Indent, "Indent", "indent");
      case "outdent":
        return toolbarButton(Outdent, "Outdent", "outdent");
      case "createLink":
        return toolbarButton(Link, "Insert Link", "createLink");
      case "insertImage":
        return !disableImages
          ? toolbarButton(Image, "Insert Image", "insertImage")
          : null;
      // case "insertVideo":
      //   return !disableVideos
      //     ? toolbarButton(Video, "Insert Video", "insertVideo")
      //     : null;
      // case "insertTable":
      //   return !disableTables
      //     ? toolbarButton(Table, "Insert Table", "insertTable")
      //     : null;
      case "insertHorizontalRule":
        return toolbarButton(Minus, "Horizontal Line", "insertHorizontalRule");
      case "formatBlock":
        return (
          <Select
            key="formatBlock"
            value={currentFormatBlock}
            onValueChange={(value) => onCommand("formatBlock", value)}
          >
            <SelectTrigger className="h-8  text-sm">
              <SelectValue placeholder="Heading" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="p" className="text-base">
                Normal
              </SelectItem>
              <SelectItem value="h1" className="text-2xl font-bold">
                Heading 1
              </SelectItem>
              <SelectItem value="h2" className="text-xl font-bold">
                Heading 2
              </SelectItem>
              <SelectItem value="h3" className="text-lg font-semibold">
                Heading 3
              </SelectItem>
              <SelectItem value="h4" className="text-base font-semibold">
                Heading 4
              </SelectItem>
              <SelectItem value="h5" className="text-sm font-semibold">
                Heading 5
              </SelectItem>
              <SelectItem value="h6" className="text-xs font-semibold">
                Heading 6
              </SelectItem>
            </SelectContent>
          </Select>
        );
      case "fontName":
        return (
          <Select
            key="fontName"
            value={currentFontName}
            onValueChange={(value) => onCommand("fontName", value)}
          >
            <SelectTrigger className="h-8 text-sm">
              <SelectValue placeholder="Font" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Arial" style={{ fontFamily: "Arial" }}>
                Arial
              </SelectItem>
              <SelectItem value="Georgia" style={{ fontFamily: "Georgia" }}>
                Georgia
              </SelectItem>
              <SelectItem
                value="Times New Roman"
                style={{ fontFamily: "Times New Roman" }}
              >
                Times New Roman
              </SelectItem>
              <SelectItem
                value="Courier New"
                style={{ fontFamily: "Courier New" }}
              >
                Courier New
              </SelectItem>
              <SelectItem value="Verdana" style={{ fontFamily: "Verdana" }}>
                Verdana
              </SelectItem>
            </SelectContent>
          </Select>
        );
      case "fontSize":
        return (
          <Select
            key="fontSize"
            value={currentFontSize}
            onValueChange={(value) => onCommand("fontSize", value)}
          >
            <SelectTrigger className="h-8 text-sm">
              <SelectValue placeholder="Size" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10px" style={{ fontSize: "10px" }}>
                10px - Tiny
              </SelectItem>
              <SelectItem value="12px" style={{ fontSize: "12px" }}>
                12px - Small
              </SelectItem>
              <SelectItem value="14px" style={{ fontSize: "14px" }}>
                14px
              </SelectItem>
              <SelectItem value="16px" style={{ fontSize: "16px" }}>
                16px - Normal
              </SelectItem>
              <SelectItem value="18px" style={{ fontSize: "18px" }}>
                18px
              </SelectItem>
              <SelectItem value="20px" style={{ fontSize: "20px" }}>
                20px
              </SelectItem>
              <SelectItem value="24px" style={{ fontSize: "24px" }}>
                24px - Large
              </SelectItem>
              <SelectItem value="28px" style={{ fontSize: "28px" }}>
                28px
              </SelectItem>
              <SelectItem value="32px" style={{ fontSize: "32px" }}>
                32px
              </SelectItem>
            </SelectContent>
          </Select>
        );
      case "foreColor":
        return (
          <ColorPicker
            key="foreColor"
            currentColor={currentForeColor}
            onColorChange={(color) => onCommand("foreColor", color)}
            title="Text Color"
            isForeground={true}
          />
        );
      case "backColor":
        return (
          <ColorPicker
            key="backColor"
            currentColor={currentBackColor}
            onColorChange={(color) => onCommand("backColor", color)}
            title="Background Color"
            isForeground={false}
          />
        );
      case "modeToggle":
        return (
          <Button
            key="modeToggle"
            type="button"
            variant="ghost"
            size="sm"
            onClick={onModeToggle}
            title={mode === "wysiwyg" ? "Code View" : "Visual View"}
            className="h-8 px-3 hover:bg-toolbar-hover hover:text-primary transition-smooth"
          >
            {mode === "wysiwyg" ? (
              <Code className="h-4 w-4 mr-1" />
            ) : (
              <Eye className="h-4 w-4 mr-1" />
            )}
            <span className="text-xs">
              {mode === "wysiwyg" ? "Code" : "Visual"}
            </span>
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1 p-2 bg-toolbar-bg border-b border-editor-border rounded-t-lg">
      {config.sections?.map((section, sectionIndex) => (
        <React.Fragment key={section.name}>
          <div className="flex items-center gap-1">
            {section.buttons
              .map((buttonType) => renderButton(buttonType))
              .filter(Boolean)}
          </div>
          {config.showSeparators &&
            sectionIndex < (config.sections?.length || 0) - 1 && (
              <Separator orientation="vertical" className="h-6 mx-1" />
            )}
        </React.Fragment>
      ))}
    </div>
  );
};
